import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../axiosConfig";
import { useSubscription } from "../../context/SubscriptionContext";
import UpgradePromptModal from "../subscription/UpgradePromptModal";
import UsageIndicator from "../subscription/UsageIndicator";
import { useNavigate } from "react-router-dom";
import { IoMdMale, IoMdFemale } from "react-icons/io";
import { CustomDatePicker } from '../commons/datepicker';
import "../../styles/TrainerAddDogModal.css";

const TrainerAddDogModal = ({ isOpen, onClose, onDogAdded }) => {
    const { t } = useTranslation(['dogs', 'common']);
    const [form, setForm] = useState({
        name: "",
        breed: "",
        dateOfBirth: "",
        approximate: false,
        gender: "",
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [limitData, setLimitData] = useState(null);
    const [isCheckingLimit, setIsCheckingLimit] = useState(false);

    const navigate = useNavigate();

    const {
        canAddDog,
        usageData,
        refreshUsageData
    } = useSubscription();

    // Check limits when modal opens
    useEffect(() => {
        if (isOpen) {
            checkDogLimit();
        }
    }, [isOpen]);

    const checkDogLimit = async () => {
        setIsCheckingLimit(true);
        try {
            const result = await canAddDog();
            if (!result.canAddDog) {
                setLimitData(result);
            } else {
                setLimitData(null);
            }
        } catch (err) {
            console.error('Error checking dog limit:', err);
        } finally {
            setIsCheckingLimit(false);
        }
    };

    const handleUpgradeClick = () => {
        handleCancel();
        navigate('/subscription/plans');
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleGenderSelect = (genderValue) => {
        setForm((prev) => ({
            ...prev,
            gender: genderValue,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name.trim()) {
            setErrors({ name: t('dogs:addDogForm.nameRequired') });
            return;
        }
        if (!form.breed.trim()) {
            setErrors({ breed: t('dogs:addDogForm.breedRequired') });
            return;
        }
        if (!form.dateOfBirth) {
            setErrors({ dateOfBirth: t('dogs:addDogForm.dateOfBirthRequired') });
            return;
        }
        if (!form.gender) {
            setErrors({ gender: t('dogs:addDogForm.genderRequired') });
            return;
        }

        // Check limit before submitting
        const limitCheck = await canAddDog();
        if (!limitCheck.canAddDog) {
            setLimitData(limitCheck);
            setShowUpgradeModal(true);
            setSubmitting(false);
            return;
        }

        try {
            setSubmitting(true);
            const response = await axios.post("/api/v1/dog/trainer/add-dog", form);
            if (onDogAdded) {
                onDogAdded(response.data);
            }
            setForm({
                name: "",
                breed: "",
                dateOfBirth: "",
                approximate: false,
                gender: "",
            });
            setErrors({});
            refreshUsageData();
            onClose();
        } catch (error) {
            console.error("Error adding dog:", error);
            if (error.response?.status === 403 && error.response?.data?.errors?.upgradeRequired) {
                const errorData = error.response.data.errors;
                setLimitData({
                    currentDogs: parseInt(errorData.currentDogs) || 0,
                    maxDogs: parseInt(errorData.maxDogs) || 0,
                    planName: errorData.planName || t('dogs:addDogForm.unknownPlan'),
                    upgradeRequired: true
                });
                setShowUpgradeModal(true);
            } else if (error.response && error.response.data) {
                if (error.response.data.errors) {
                    setErrors(error.response.data.errors);
                } else if (error.response.data.message) {
                    setErrors({ general: error.response.data.message });
                } else {
                    setErrors(error.response.data);
                }
            } else {
                setErrors({ general: t('dogs:addDogForm.addError') });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        setForm({
            name: "",
            breed: "",
            dateOfBirth: "",
            approximate: false,
            gender: "",
        });
        setErrors({});
        setLimitData(null);
        setShowUpgradeModal(false);
        onClose();
    };

    if (!isOpen) return null;

    // Show upgrade modal if limit is reached
    if (showUpgradeModal && limitData) {
        return (
            <UpgradePromptModal
                isOpen={true}
                onClose={() => setShowUpgradeModal(false)}
                currentDogs={limitData.currentDogs}
                maxDogs={limitData.maxDogs}
                planName={limitData.planName}
                suggestedPlanName={limitData.suggestedUpgradePlanName}
                availableUpgrades={limitData.availableUpgrades}
            />
        );
    }

    const isAtLimit = limitData && !limitData.canAddDog;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCancel();
        }
    };

    return (
        <div className="trainerAddDogModal__overlay" onClick={handleOverlayClick}>
            <div className="trainerAddDogModal__content">
                <h2 className="trainerAddDogModal__title">{t('dogs:addDogForm.title')}</h2>

                {/* Show usage indicator */}
                {usageData && (
                    <div style={{ marginBottom: '16px' }}>
                        <UsageIndicator
                            currentDogs={usageData.currentDogs}
                            maxDogs={usageData.maxDogs}
                            planName={usageData.planName}
                            showUpgradeLink={false}
                            compact={true}
                        />
                    </div>
                )}

                {/* Show limit reached message */}
                {isAtLimit && (
                    <div className="trainerAddDogModal__generalError">
                        {t('dogs:addDogForm.limitReached')}
                        <button
                            type="button"
                            style={{ marginLeft: '8px', cursor: 'pointer', color: '#7269FF', background: 'none', border: 'none', textDecoration: 'underline' }}
                            onClick={handleUpgradeClick}
                        >
                            {t('dogs:addDogForm.upgradePlan')}
                        </button>
                    </div>
                )}

                {errors.general && (
                    <div className="trainerAddDogModal__generalError">{errors.general}</div>
                )}

                <form className="trainerAddDogModal__form">
                    <div className="trainerAddDogModal__formGroup">
                        <label htmlFor="trainer-dog-name" className="trainerAddDogModal__label">{t('dogs:addDogForm.dogName')}</label>
                        <input
                            id="trainer-dog-name"
                            type="text"
                            name="name"
                            className="trainerAddDogModal__input"
                            placeholder={t('dogs:addDogForm.dogNamePlaceholder')}
                            value={form.name}
                            onChange={handleChange}
                            required
                        />
                        {errors.name && <span className="trainerAddDogModal__error">{errors.name}</span>}
                    </div>

                    <div className="trainerAddDogModal__formGroup">
                        <label htmlFor="trainer-dog-breed" className="trainerAddDogModal__label">{t('dogs:breed')}</label>
                        <input
                            id="trainer-dog-breed"
                            type="text"
                            name="breed"
                            className="trainerAddDogModal__input"
                            placeholder={t('dogs:addDogForm.breedPlaceholder')}
                            value={form.breed}
                            onChange={handleChange}
                            required
                        />
                        {errors.breed && <span className="trainerAddDogModal__error">{errors.breed}</span>}
                    </div>

                    <div className="trainerAddDogModal__formGroup">
                        <label htmlFor="trainer-dog-dob" className="trainerAddDogModal__label">{t('dogs:dateOfBirth')}</label>
                        <CustomDatePicker
                            id="trainer-dog-dob"
                            name="dateOfBirth"
                            className="trainerAddDogModal__input"
                            value={form.dateOfBirth}
                            onChange={handleChange}
                            max={new Date().toISOString().split('T')[0]}
                        />
                        {errors.dateOfBirth && <span className="trainerAddDogModal__error">{errors.dateOfBirth}</span>}
                    </div>

                    <div className="trainerAddDogModal__checkboxGroup">
                        <label htmlFor="trainer-dog-approximate" className="trainerAddDogModal__checkboxLabel">
                            {t('dogs:approximateAge')}
                            <input
                                id="trainer-dog-approximate"
                                type="checkbox"
                                name="approximate"
                                className="trainerAddDogModal__checkbox"
                                checked={form.approximate}
                                onChange={handleChange}
                            />
                        </label>
                    </div>

                    <div className="trainerAddDogModal__formGroup">
                        <label className="trainerAddDogModal__label">{t('dogs:gender')}</label>
                        <div className="trainerAddDogModal__genderSelector">
                            <span
                                className={`trainerAddDogModal__genderButton ${form.gender === "MALE" ? "trainerAddDogModal__genderButton--selected" : ""}`}
                                onClick={() => handleGenderSelect("MALE")}
                                role="button"
                                tabIndex="0"
                                aria-label={t('dogs:male')}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        handleGenderSelect("MALE");
                                    }
                                }}
                            >
                                <IoMdMale className="trainerAddDogModal__genderIcon" size={32} />
                                <span style={{ marginLeft: "8px" }}>{t('dogs:male')}</span>
                            </span>
                            <span
                                className={`trainerAddDogModal__genderButton ${form.gender === "FEMALE" ? "trainerAddDogModal__genderButton--selected" : ""}`}
                                onClick={() => handleGenderSelect("FEMALE")}
                                role="button"
                                tabIndex="0"
                                aria-label={t('dogs:female')}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        handleGenderSelect("FEMALE");
                                    }
                                }}
                            >
                                <IoMdFemale className="trainerAddDogModal__genderIcon" size={32} />
                                <span style={{ marginLeft: "8px" }}>{t('dogs:female')}</span>
                            </span>
                        </div>
                        {errors.gender && <span className="trainerAddDogModal__error">{errors.gender}</span>}
                    </div>

                    <div className="trainerAddDogModal__actions">
                        <button
                            type="submit"
                            className="trainerAddDogModal__submitButton"
                            onClick={handleSubmit}
                            disabled={submitting || isCheckingLimit || isAtLimit}
                        >
                            {submitting ? t('dogs:addDogForm.adding') : isCheckingLimit ? t('dogs:addDogForm.checking') : t('common:add')}
                        </button>
                        <button type="button" className="trainerAddDogModal__cancelButton" onClick={handleCancel}>
                            {t('common:cancel')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TrainerAddDogModal;
